import { motion } from "framer-motion";
import { Radar, KeyRound, ScanEye, Route, BadgeCheck, ArrowRight } from "lucide-react";
import { CAREERS, PROGRESSION, COURSE } from "@/data/course";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";

const CAREER_ICONS = { radar: Radar, key: KeyRound, scan: ScanEye, route: Route };

export const Pathway = () => (
  <section id="pathway" className="relative py-24 sm:py-32" data-testid="pathway-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow no="06">Certification Pathway</Eyebrow>
        <SectionTitle className="mt-5 max-w-3xl">
          A credential that maps to emerging quantum-security roles.
        </SectionTitle>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
        {/* certificate preview */}
        <Reveal className="lg:col-span-5" delay={0.05}>
          <motion.div
            whileHover={{ rotate: 0, scale: 1.015 }}
            initial={{ rotate: -1.5 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-2xl border-2 border-[#D4AF37]/45 bg-gradient-to-br from-[#2B0B1E] via-[#180C1F] to-[#0B060F] p-8 shadow-[0_30px_80px_rgba(43,11,30,0.6)]"
            data-testid="certificate-preview"
          >
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{
                background:
                  "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(212,175,55,0.12), transparent 70%)",
              }}
              aria-hidden="true"
            />
            <div className="relative flex flex-col items-center text-center">
              <img src={COURSE.logo} alt="IMMUTA" className="h-10 w-auto" />
              <span className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                Certificate of Competency
              </span>
              <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold text-[#F4F1F8]">
                Immuta Quantum Security Specialist Certificate
              </h3>
              <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#9A36E3]/40 bg-[#9A36E3]/12 px-4 py-2 font-mono2 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-[#E4CBFF]">
                <BadgeCheck className="h-3.5 w-3.5 text-[#C77DFF]" />
                {COURSE.competency}
              </span>
              <div className="mt-8 flex w-full items-end justify-between border-t border-[#D4AF37]/20 pt-5">
                <span className="font-mono2 text-[10px] tracking-[0.2em] text-[#7A6D8C]">
                  ID · IMM-QSS-L2-2026
                </span>
                <span
                  className="h-12 w-12 rounded-full border-2 border-[#D4AF37]/60"
                  style={{
                    background:
                      "conic-gradient(from 200deg, #D4AF37 0%, #2B0B1E 35%, #9A36E3 65%, #D4AF37 100%)",
                  }}
                  aria-hidden="true"
                />
              </div>
            </div>
          </motion.div>
        </Reveal>

        {/* careers */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="text-sm sm:text-base text-[#B3A7C2]">
              This certification develops capability relevant to emerging roles such as:
            </p>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CAREERS.map((c, i) => {
              const Icon = CAREER_ICONS[c.icon];
              return (
                <Reveal key={c.role} delay={0.08 * i} className="h-full">
                  <article
                    className="group flex h-full items-center gap-4 rounded-2xl border border-[#D4AF37]/18 bg-gradient-to-br from-[#2B0B1E]/50 to-[#180C1F] p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#D4AF37]/45 hover:shadow-[0_18px_44px_rgba(212,175,55,0.14)]"
                    data-testid={`career-card-${i + 1}`}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#9A36E3]/35 bg-[#9A36E3]/10 text-[#C77DFF] transition-transform duration-500 group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-base sm:text-lg font-bold text-[#F4F1F8]">
                      {c.role}
                    </h3>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {/* progression */}
          <Reveal delay={0.15}>
            <div
              className="mt-8 rounded-2xl border border-[#D4AF37]/18 bg-[#0B060F]/70 p-7 sm:p-8"
              data-testid="progression-path"
            >
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                Progression Path
              </p>
              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                {PROGRESSION.map((p, i) => (
                  <div key={p.step} className="flex flex-1 items-center gap-4 sm:gap-3">
                    <div
                      className={`flex-1 rounded-xl border p-4 transition-colors ${
                        p.current
                          ? "border-[#FF5E36]/50 bg-[#FF5E36]/10"
                          : "border-[#D4AF37]/15 bg-[#2B0B1E]/30"
                      }`}
                      data-testid={`progression-step-${i + 1}`}
                    >
                      <span
                        className={`font-mono2 text-[10px] uppercase tracking-[0.2em] ${
                          p.current ? "text-[#FF5E36]" : "text-[#7A6D8C]"
                        }`}
                      >
                        {p.level} {p.current ? "· You are here" : ""}
                      </span>
                      <p className="mt-1.5 font-display text-sm sm:text-base font-bold text-[#F4F1F8]">
                        {p.step}
                      </p>
                    </div>
                    {i < PROGRESSION.length - 1 && (
                      <ArrowRight
                        className="hidden h-4 w-4 shrink-0 text-[#D4AF37]/50 sm:block"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#B3A7C2]">
                The Security Specialist pathway is the critical bridge between understanding
                quantum computing and engineering quantum-safe enterprise environments.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
